import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Navbar, Footer } from '../components';
import { setCredentials } from '../redux/action/authSlice';
import toast from 'react-hot-toast';
import api from '../utils/api';

const InputField = ({ label, id, type = "text", value, onChange, placeholder, disabled = false }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-sm font-semibold text-zinc-300">
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
    />
  </div>
);

const SelectField = ({ label, id, value, onChange, options }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-sm font-semibold text-zinc-300">
      {label}
    </label>
    <select
      id={id}
      value={value}
      onChange={onChange}
      className="w-full rounded-xl bg-zinc-900 border border-white/10 px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition"
    >
      <option value="" disabled>Pilih {label}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

const Profile = () => {
  const { user, token, isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    password: '',
    password_confirmation: '',
  });
  
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        date_of_birth: user.date_of_birth ? user.date_of_birth.split('T')[0] : '',
        gender: user.gender || '',
        password: '',
        password_confirmation: '',
      });
      setAvatarPreview(user.avatar || null);
    }
  }, [user, isAuthenticated, navigate]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Basic validation
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Ukuran file maksimal 2MB");
        return;
      }
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleCopyReferral = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      toast.success("Kode Referral disalin!");
    }
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(angka || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append('_method', 'POST'); // Form spoofing if needed, though route says POST
      
      data.append('name', formData.name);
      data.append('email', formData.email);
      
      if (formData.phone) data.append('phone', formData.phone);
      if (formData.date_of_birth) data.append('date_of_birth', formData.date_of_birth);
      if (formData.gender) data.append('gender', formData.gender);
      
      if (formData.password) {
        data.append('password', formData.password);
        data.append('password_confirmation', formData.password_confirmation);
      }
      
      if (avatarFile) {
        data.append('avatar', avatarFile);
      }

      const response = await api.post('/api/auth/profile', data);

      if (response.data.user) {
        // Update Redux state
        dispatch(setCredentials({ user: response.data.user, token }));
        toast.success(response.data.message || "Profil berhasil diperbarui!");
        
        // Reset password fields
        setFormData(prev => ({ ...prev, password: '', password_confirmation: '' }));
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Gagal memperbarui profil";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-zinc-950 text-white pb-20 pt-10 px-4 sm:px-6 lg:px-8 selection:bg-emerald-500/30">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-extrabold tracking-tight">Profil Saya</h1>
            <p className="mt-2 text-sm text-zinc-400">Atur informasi data diri dan preferensi akun Anda.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Read Only Info & Avatar */}
            <div className="col-span-1 space-y-6">
              
              {/* Avatar Card */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 text-center shadow-xl">
                <div className="relative inline-block group">
                  <div className="h-32 w-32 rounded-full ring-4 ring-emerald-500/20 overflow-hidden bg-zinc-800 mx-auto flex items-center justify-center relative">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-4xl font-bold text-emerald-500 uppercase">
                        {user.name?.charAt(0) || "U"}
                      </span>
                    )}
                    
                    {/* Hover Overlay */}
                    <div 
                      className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                       <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                       </svg>
                    </div>
                  </div>
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="hidden" 
                    accept="image/*" 
                    onChange={handleFileChange} 
                  />
                  <button 
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-4 text-xs font-semibold bg-white/10 hover:bg-white/20 text-white py-1.5 px-4 rounded-full transition"
                  >
                    Ganti Foto
                  </button>
                </div>
                
                <h3 className="mt-4 font-bold text-lg text-white truncate">{user.name}</h3>
                <p className="text-sm text-emerald-400 capitalize">{user.role}</p>
                <div className="mt-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-zinc-800 text-zinc-300 ring-1 ring-inset ring-zinc-700">
                  Status: <span className={user.status === 'active' ? 'ml-1 text-emerald-400' : 'ml-1 text-rose-400'}>{user.status}</span>
                </div>
              </div>

              {/* Wallet & Referral Card */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 shadow-xl space-y-4">
                <div>
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Saldo Wallet</h4>
                  <p className="text-2xl font-bold text-white">{formatRupiah(user.balance)}</p>
                </div>
                <div className="h-px w-full bg-zinc-800 my-4" />
                <div>
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Kode Referral</h4>
                  <div className="flex items-center gap-2">
                    <code className="text-lg font-mono font-bold text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-lg">
                      {user.referral_code || 'TIDAK ADA'}
                    </code>
                    {user.referral_code && (
                      <button 
                        onClick={handleCopyReferral}
                        className="p-2 text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition"
                        title="Salin Kode"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="col-span-1 lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 lg:p-8 shadow-xl">
                <h3 className="text-xl font-bold text-white border-b border-zinc-800 pb-4 mb-6">Informasi Pribadi</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <InputField label="Nama Lengkap *" id="name" value={formData.name} onChange={handleInputChange} />
                  <InputField label="Alamat Email *" id="email" type="email" value={formData.email} onChange={handleInputChange} />
                  <InputField label="Nomor Telepon" id="phone" value={formData.phone} onChange={handleInputChange} placeholder="081234..." />
                  <InputField label="Tanggal Lahir" id="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleInputChange} />
                  
                  <SelectField 
                    label="Jenis Kelamin" 
                    id="gender" 
                    value={formData.gender} 
                    onChange={handleInputChange}
                    options={[
                      { value: 'male', label: 'Laki-laki' },
                      { value: 'female', label: 'Perempuan' },
                      { value: 'other', label: 'Lainnya' }
                    ]}
                  />
                </div>

                <h3 className="text-xl font-bold text-white border-b border-zinc-800 pb-4 mb-6 mt-8">Keamanan</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <InputField 
                    label="Password Baru" 
                    id="password" 
                    type="password" 
                    value={formData.password} 
                    onChange={handleInputChange} 
                    placeholder="Minimal 8 karakter" 
                  />
                  <InputField 
                    label="Konfirmasi Password" 
                    id="password_confirmation" 
                    type="password" 
                    value={formData.password_confirmation} 
                    onChange={handleInputChange} 
                    placeholder="Ulangi password baru" 
                  />
                </div>
                <p className="text-xs text-zinc-500 mb-6">* Kosongkan kolom password jika Anda tidak ingin mengubahnya.</p>

                <div className="flex justify-end pt-4 border-t border-zinc-800">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-emerald-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading && (
                      <div className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    )}
                    {loading ? "Menyimpan..." : "Simpan Perubahan"}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Profile;
